import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Text,
  useFileUpload,
} from '@chakra-ui/react';
import { LuDownload, LuTrash2, LuUpload } from 'react-icons/lu';
import {
  deleteDocument,
  documentContentUrl,
  listClientDocuments,
  listContractDocuments,
  uploadClientDocument,
  uploadContractDocument,
  type DocumentKind,
  type DocumentRow,
} from '@/lib/api';
import { toaster } from '@/components/ui/toaster';
import {
  FileUploadHiddenInput,
  FileUploadList,
  FileUploadRootProvider,
  FileUploadTrigger,
} from '@/components/ui/file-upload';

const KIND_LABELS: Record<DocumentKind, string> = {
  dpi: 'DPI',
  contrato: 'Contrato',
  recibo: 'Recibo',
  voucher: 'Voucher',
  otro: 'Otro',
};
const KIND_ORDER: DocumentKind[] = ['dpi', 'contrato', 'recibo', 'voucher', 'otro'];
const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_BYTES = 10 * 1024 * 1024;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type DocumentsParent = { type: 'client' | 'contract'; id: string };

export function DocumentsPanel({ parent, canDelete }: { parent: DocumentsParent; canDelete: boolean }) {
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<DocumentKind>(parent.type === 'contract' ? 'contrato' : 'dpi');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileUpload = useFileUpload({
    maxFiles: 1,
    accept: ACCEPT,
    maxFileSize: MAX_BYTES,
    onFileReject: (details) => {
      const codes = new Set(details.files.flatMap((f) => f.errors));
      if (codes.has('FILE_TOO_LARGE')) {
        toaster.create({ title: 'Archivo demasiado grande', description: 'Máximo 10 MB.', type: 'error' });
      } else if (codes.has('FILE_INVALID_TYPE')) {
        toaster.create({ title: 'Formato no permitido', description: 'Usa PDF, JPG o PNG.', type: 'error' });
      } else {
        toaster.create({ title: 'Archivo no válido', type: 'error' });
      }
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows =
        parent.type === 'client'
          ? await listClientDocuments(parent.id)
          : await listContractDocuments(parent.id);
      setDocs(rows);
    } catch (err) {
      toaster.create({
        title: 'No se pudieron cargar los documentos',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [parent.type, parent.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onUpload() {
    const file = fileUpload.acceptedFiles[0];
    if (!file) {
      toaster.create({ title: 'Selecciona un archivo', type: 'error' });
      return;
    }
    setBusy(true);
    try {
      if (parent.type === 'client') {
        await uploadClientDocument(parent.id, file, kind, label);
      } else {
        await uploadContractDocument(parent.id, file, kind, label);
      }
      setLabel('');
      fileUpload.clearFiles();
      toaster.create({ title: 'Documento subido', type: 'success' });
      await load();
    } catch (err) {
      toaster.create({
        title: 'No se pudo subir el documento',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(doc: DocumentRow) {
    if (deletingId) return;
    if (!window.confirm(`¿Eliminar "${doc.label || doc.originalFilename}"?`)) return;
    setDeletingId(doc.id);
    try {
      await deleteDocument(doc.id);
      toaster.create({ title: 'Documento eliminado', type: 'success' });
      await load();
    } catch (err) {
      toaster.create({
        title: 'No se pudo eliminar',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  }

  const groups = KIND_ORDER.map((k) => ({ kind: k, items: docs.filter((d) => d.kind === k) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <Stack gap="4">
      <Box borderWidth="1px" borderColor="border" rounded="md" p="3">
        <Heading size="xs" mb="2">
          Subir documento
        </Heading>
        <Stack gap="2">
          <Stack direction={{ base: 'column', md: 'row' }} gap="2" align={{ base: 'stretch', md: 'end' }}>
            <NativeSelect.Root size="sm" maxW={{ md: '40' }}>
              <NativeSelect.Field
                value={kind}
                onChange={(e) => setKind(e.currentTarget.value as DocumentKind)}
              >
                {KIND_ORDER.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Input
              size="sm"
              placeholder="Nombre (opcional)"
              value={label}
              onChange={(e) => setLabel(e.currentTarget.value)}
              maxLength={120}
            />
          </Stack>
          <FileUploadRootProvider value={fileUpload}>
            <FileUploadHiddenInput />
            <Stack direction={{ base: 'column', sm: 'row' }} gap="2" align={{ base: 'stretch', sm: 'center' }}>
              <FileUploadTrigger asChild>
                <Button size="sm" variant="outline" flexShrink={0}>
                  <Icon as={LuUpload} /> Seleccionar archivo
                </Button>
              </FileUploadTrigger>
              <Button size="sm" onClick={onUpload} loading={busy} flexShrink={0}>
                Subir
              </Button>
            </Stack>
            <FileUploadList showSize clearable />
          </FileUploadRootProvider>
        </Stack>
      </Box>

      {loading ? (
        <HStack color="fg.muted">
          <Spinner size="sm" /> <Text>Cargando…</Text>
        </HStack>
      ) : groups.length === 0 ? (
        <Text color="fg.muted">Aún no hay documentos.</Text>
      ) : (
        <Stack gap="3">
          {groups.map((g) => (
            <Box key={g.kind}>
              <Heading size="xs" color="fg.muted" mb="1">
                {KIND_LABELS[g.kind]}
              </Heading>
              <Stack gap="1">
                {g.items.map((d) => (
                  <HStack
                    key={d.id}
                    justify="space-between"
                    borderWidth="1px"
                    borderColor="border"
                    rounded="md"
                    px="3"
                    py="2"
                  >
                    <Stack gap="0" minW={0}>
                      <Text fontWeight="medium" truncate>
                        {d.label || d.originalFilename}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        {fmtSize(d.fileSize)} · {new Date(d.uploadedAt).toLocaleDateString('es-GT')}
                        {d.uploadedByName ? ` · ${d.uploadedByName}` : ''}
                      </Text>
                    </Stack>
                    <HStack gap="1" flexShrink={0}>
                      <Button size="xs" variant="ghost" asChild title="Descargar">
                        <a href={documentContentUrl(d.id)} target="_blank" rel="noreferrer">
                          <Icon as={LuDownload} />
                        </a>
                      </Button>
                      {canDelete && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => onDelete(d)}
                          loading={deletingId === d.id}
                          disabled={deletingId !== null}
                          title="Eliminar"
                        >
                          <Icon as={LuTrash2} />
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
